<template>
    <transition name="editor-fade">
        <div v-if="isOpen" class="image-editor-overlay">

            <!-- Toolbar superior -->
            <div class="editor-toolbar">
                <button type="button" class="btn-tool btn-cancel" title="Descartar edição" @click="cancel">
                    <i class="ri-arrow-left-line"></i>
                </button>

                <div class="tools-group">
                    <button type="button" class="btn-tool" :class="{ active: tool === 'crop' }" title="Recortar"
                        @click="setTool('crop')">
                        <i class="ri-crop-line"></i>
                    </button>
                    <button type="button" class="btn-tool" :class="{ active: tool === 'draw' }" title="Desenhar"
                        @click="setTool('draw')">
                        <i class="ri-pencil-line"></i>
                    </button>
                    <button type="button" class="btn-tool" :class="{ active: tool === 'text' }" title="Adicionar texto"
                        @click="setTool('text')">
                        <i class="ri-text"></i>
                    </button>
                    <button type="button" class="btn-tool" :class="{ active: tool === 'shape' }" title="Formas"
                        @click="setTool('shape')">
                        <i class="ri-shape-line"></i>
                    </button>
                    <button type="button" class="btn-tool" :disabled="!canUndo" title="Desfazer" @click="undo">
                        <i class="ri-arrow-go-back-line"></i>
                    </button>
                </div>

                <button type="button" class="btn-tool btn-done" title="Concluir" @click="done">
                    <i class="ri-check-line"></i>
                </button>
            </div>

            <!-- Área do canvas -->
            <div ref="stageRef" class="editor-stage" @pointerdown.self="onStageDown">
                <div ref="wrapperRef" class="canvas-wrapper">
                    <canvas ref="canvasRef" class="editor-canvas" @pointerdown="onPointerDown"
                        @pointermove="onPointerMove" @pointerup="onPointerUp" @pointerleave="onPointerUp"></canvas>

                    <!-- Camada de caixas de texto: visível em todos os modos, editável só no modo texto -->
                    <div v-if="textBoxes.length" class="text-layer" :class="{ interactive: tool === 'text' }">
                        <div v-for="box in textBoxes" :key="box.id" class="text-box"
                            :class="{ selected: tool === 'text' && box.id === selectedTextId }"
                            :style="boxWrapperStyle(box)" @pointerdown="onTextBodyDown($event, box)"
                            @pointermove="onTextBodyMove" @pointerup="onTextBodyUp">
                            <span v-if="tool === 'text' && box.id === selectedTextId" class="text-rotate-handle"
                                title="Girar" @pointerdown.stop="startTextRotate($event, box)"
                                @pointermove="onTextRotateMove" @pointerup="onTextRotateEnd"></span>
                            <div class="text-edit" :id="'ta-' + box.id" :contenteditable="tool === 'text'"
                                spellcheck="false" :style="textEditStyle(box)" v-init-text="box.value"
                                @input="onTextInput($event, box)" @focus="selectText(box)"></div>
                            <span v-if="tool === 'text' && box.id === selectedTextId" class="text-resize-handle"
                                title="Ajustar tamanho" @pointerdown.stop="startTextResize($event, box)"
                                @pointermove="onTextResizeMove" @pointerup="onTextResizeEnd"></span>
                        </div>
                    </div>

                    <!-- Camada de formas: visível em todos os modos, editável só no modo Formas -->
                    <svg v-if="shapeBoxes.length" class="shape-svg-layer" :class="{ interactive: tool === 'shape' }"
                        :width="dispW" :height="dispH" :viewBox="`0 0 ${dispW} ${dispH}`">
                        <g v-for="box in shapeBoxes" :key="box.id">
                            <!-- Retângulo -->
                            <template v-if="box.type === 'rect'">
                                <rect :x="boxPx(box).x" :y="boxPx(box).y" :width="boxPx(box).w" :height="boxPx(box).h"
                                    :transform="rotAttr(box)" :fill="box.fill ? box.color : 'none'"
                                    :stroke="box.fill ? 'none' : box.color" :stroke-width="box.lineWidth"
                                    stroke-linejoin="round" />
                                <rect class="shape-hit" :x="boxPx(box).x" :y="boxPx(box).y" :width="boxPx(box).w"
                                    :height="boxPx(box).h" :transform="rotAttr(box)"
                                    @pointerdown.stop="onShapeBodyDown($event, box)" @pointermove="onShapeBodyMove"
                                    @pointerup="onShapeBodyUp" />
                            </template>
                            <!-- Elipse / Círculo -->
                            <template v-else-if="box.type === 'ellipse'">
                                <ellipse :cx="boxPx(box).x + boxPx(box).w / 2" :cy="boxPx(box).y + boxPx(box).h / 2"
                                    :rx="boxPx(box).w / 2" :ry="boxPx(box).h / 2" :transform="rotAttr(box)"
                                    :fill="box.fill ? box.color : 'none'" :stroke="box.fill ? 'none' : box.color"
                                    :stroke-width="box.lineWidth" />
                                <ellipse class="shape-hit" :cx="boxPx(box).x + boxPx(box).w / 2"
                                    :cy="boxPx(box).y + boxPx(box).h / 2" :rx="boxPx(box).w / 2" :ry="boxPx(box).h / 2"
                                    :transform="rotAttr(box)" @pointerdown.stop="onShapeBodyDown($event, box)"
                                    @pointermove="onShapeBodyMove" @pointerup="onShapeBodyUp" />
                            </template>
                            <!-- Linha / Seta -->
                            <template v-else>
                                <line :x1="segPx(box).x1" :y1="segPx(box).y1" :x2="segPx(box).x2" :y2="segPx(box).y2"
                                    :stroke="box.color" :stroke-width="box.lineWidth" stroke-linecap="round" />
                                <polygon v-if="box.type === 'arrow'" :points="arrowHeadPoints(box)" :fill="box.color" />
                                <line class="shape-hit shape-hit-line" :x1="segPx(box).x1" :y1="segPx(box).y1"
                                    :x2="segPx(box).x2" :y2="segPx(box).y2"
                                    :stroke-width="Math.max(box.lineWidth + 16, 22)"
                                    @pointerdown.stop="onShapeBodyDown($event, box)" @pointermove="onShapeBodyMove"
                                    @pointerup="onShapeBodyUp" />
                            </template>
                        </g>
                    </svg>

                    <!-- Overlay de criação de forma (arrastar em área vazia) -->
                    <div v-if="tool === 'shape'" class="shape-create-overlay" @pointerdown="onShapeCreateDown"
                        @pointermove="onShapeCreateMove" @pointerup="onShapeCreateUp"
                        @pointerleave="onShapeCreateUp"></div>

                    <!-- Handles da forma selecionada -->
                    <svg v-if="selectedHandles" class="shape-handles-layer" :width="dispW" :height="dispH"
                        :viewBox="`0 0 ${dispW} ${dispH}`">
                        <template v-if="selectedHandles.kind === 'box'">
                            <polygon class="shape-outline" :points="selectedHandles.outline" />
                            <line class="shape-rotate-stem" :x1="selectedHandles.top.x" :y1="selectedHandles.top.y"
                                :x2="selectedHandles.rotate.x" :y2="selectedHandles.rotate.y" />
                            <circle class="shape-handle shape-handle-rotate" :cx="selectedHandles.rotate.x"
                                :cy="selectedHandles.rotate.y" r="8"
                                @pointerdown.stop="startShapeRotate($event, selectedShape)"
                                @pointermove="onShapeRotateMove" @pointerup="onShapeRotateEnd" />
                            <circle v-for="c in selectedHandles.corners" :key="c.key" class="shape-handle" :cx="c.x"
                                :cy="c.y" r="7" @pointerdown.stop="startShapeResize($event, c.key, selectedShape)"
                                @pointermove="onShapeResizeMove" @pointerup="onShapeResizeEnd" />
                        </template>
                        <template v-else>
                            <circle class="shape-handle" :cx="selectedHandles.p1.x" :cy="selectedHandles.p1.y" r="8"
                                @pointerdown.stop="startShapeEndpoint($event, selectedShape, 1)"
                                @pointermove="onShapeEndpointMove" @pointerup="onShapeEndpointEnd" />
                            <circle class="shape-handle" :cx="selectedHandles.p2.x" :cy="selectedHandles.p2.y" r="8"
                                @pointerdown.stop="startShapeEndpoint($event, selectedShape, 2)"
                                @pointermove="onShapeEndpointMove" @pointerup="onShapeEndpointEnd" />
                        </template>
                    </svg>

                    <!-- Overlay de recorte -->
                    <div v-if="tool === 'crop'" class="interaction-overlay" @pointermove="onOverlayPointerMove"
                        @pointerup="onOverlayPointerUp" @pointerleave="onOverlayPointerUp">
                        <div v-if="cropRect" class="crop-rect" :style="cropRectStyle" @pointerdown.stop="startMoveCrop">
                            <span class="crop-grid"></span>
                            <span v-for="h in cropHandles" :key="h" class="crop-handle" :class="'handle-' + h"
                                @pointerdown.stop="startResizeCrop($event, h)"></span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Seletor de emoji (texto) -->
            <transition name="editor-slide">
                <div v-if="tool === 'text' && isEmojiOpen" class="editor-emoji-picker" @pointerdown.stop>
                    <EmojiPickerField :theme="'dark'" @select="onSelectEmoji" />
                </div>
            </transition>

            <!-- Seletor de cor (espectro completo + hex) -->
            <transition name="editor-slide">
                <div v-if="isColorPickerOpen" class="color-picker-panel" @pointerdown.stop>
                    <canvas ref="spectrumRef" class="spectrum-canvas" width="260" height="150"
                        @pointerdown="onSpectrumDown" @pointermove="onSpectrumMove" @pointerup="onSpectrumUp"
                        @pointerleave="onSpectrumUp"></canvas>
                    <div class="hex-row">
                        <span class="hex-preview" :style="{ backgroundColor: color }"></span>
                        <input type="text" class="hex-input" v-model="hexInput" maxlength="7" spellcheck="false"
                            @keydown.enter="applyHex" @blur="applyHex">
                    </div>
                </div>
            </transition>

            <!-- Barra inferior de controles -->
            <div class="editor-bottom">

                <!-- Controles de DESENHO -->
                <transition name="editor-slide">
                    <div v-if="tool === 'draw'" class="control-bar">
                        <div class="color-palette">
                            <button v-for="c in colors" :key="c" type="button" class="color-swatch"
                                :class="{ active: color === c }" :style="{ backgroundColor: c }"
                                @click="applyColor(c)"></button>
                            <button type="button" class="color-swatch swatch-custom"
                                :class="{ active: isColorPickerOpen }" title="Mais cores"
                                @click.stop="toggleColorPicker"></button>
                        </div>
                        <div class="thickness-control">
                            <i class="ri-line-height"></i>
                            <input type="range" min="2" max="28" step="1" v-model.number="lineWidth">
                        </div>
                    </div>
                </transition>

                <!-- Controles de FORMAS -->
                <transition name="editor-slide">
                    <div v-if="tool === 'shape'" class="control-bar shape-controls">
                        <div class="shape-type-group">
                            <button v-for="s in shapeTypes" :key="s.value" type="button" class="btn-tool btn-bottom"
                                :class="{ active: shapeType === s.value }" :title="s.label" @click="setShapeType(s.value)">
                                <i :class="s.icon"></i>
                            </button>
                        </div>
                        <div class="color-palette">
                            <button v-for="c in colors" :key="c" type="button" class="color-swatch"
                                :class="{ active: color === c }" :style="{ backgroundColor: c }"
                                @click="applyColor(c)"></button>
                            <button type="button" class="color-swatch swatch-custom"
                                :class="{ active: isColorPickerOpen }" title="Mais cores"
                                @click.stop="toggleColorPicker"></button>
                        </div>
                        <div class="thickness-control">
                            <i class="ri-line-height"></i>
                            <input type="range" min="2" max="28" step="1" v-model.number="lineWidth">
                        </div>
                        <button v-if="fillApplicable" type="button" class="ctrl-pill" :class="{ active: fillActive }"
                            title="Preencher" @click="toggleShapeFill">
                            <span class="bg-dot" :class="{ on: fillActive }"></span>
                            Preencher
                        </button>
                        <button type="button" class="btn-tool btn-bottom" :disabled="!selectedShape" title="Excluir forma"
                            @click="deleteSelectedShape">
                            <i class="ri-delete-bin-line"></i>
                        </button>
                    </div>
                </transition>

                <!-- Controles de TEXTO -->
                <transition name="editor-slide">
                    <div v-if="tool === 'text'" class="control-bar text-controls">
                        <div class="color-palette">
                            <button v-for="c in colors" :key="c" type="button" class="color-swatch"
                                :class="{ active: color === c }" :style="{ backgroundColor: c }"
                                @click="applyColor(c)"></button>
                            <button type="button" class="color-swatch swatch-custom"
                                :class="{ active: isColorPickerOpen }" title="Mais cores"
                                @click.stop="toggleColorPicker"></button>
                        </div>

                        <div class="font-dropdown">
                            <button type="button" class="ctrl-pill" title="Fonte" @click.stop="toggleFontMenu">
                                <span :style="{ fontFamily: font }">{{ fontLabel }}</span>
                                <i class="ri-arrow-down-s-line"></i>
                            </button>
                            <transition name="editor-slide">
                                <div v-if="isFontOpen" class="font-menu" @pointerdown.stop>
                                    <button v-for="f in fonts" :key="f.value" type="button" class="font-option"
                                        :class="{ active: font === f.value }" :style="{ fontFamily: f.value }"
                                        @click="selectFont(f.value)">{{ f.label }}</button>
                                </div>
                            </transition>
                        </div>

                        <button type="button" class="ctrl-pill" :class="{ active: bg }" title="Plano de fundo"
                            @click="toggleBg">
                            <span class="bg-dot" :class="{ on: bg }"></span>
                            Plano de fundo
                        </button>

                        <button type="button" class="btn-tool btn-bottom" :class="{ active: isEmojiOpen }" title="Emoji"
                            @click.stop="toggleEmoji">
                            <i class="ri-emotion-happy-line"></i>
                        </button>

                        <button type="button" class="btn-tool btn-bottom" :disabled="!selectedBox" title="Excluir texto"
                            @click="deleteSelectedText">
                            <i class="ri-delete-bin-line"></i>
                        </button>
                    </div>
                </transition>

                <!-- Controles de RECORTE -->
                <transition name="editor-slide">
                    <div v-if="tool === 'crop'" class="control-bar crop-actions">
                        <button type="button" class="btn-crop-action btn-crop-cancel" @click="resetCrop">
                            <i class="ri-refresh-line"></i> Redefinir
                        </button>
                        <div class="crop-transform">
                            <button type="button" class="btn-tool btn-bottom" title="Girar à esquerda"
                                @click="rotateImage(-90)">
                                <i class="ri-anticlockwise-line"></i>
                            </button>
                            <button type="button" class="btn-tool btn-bottom" title="Girar à direita"
                                @click="rotateImage(90)">
                                <i class="ri-clockwise-line"></i>
                            </button>
                            <button type="button" class="btn-tool btn-bottom" title="Espelhar horizontal"
                                @click="flipImage(true)">
                                <i class="ri-flip-horizontal-line"></i>
                            </button>
                            <button type="button" class="btn-tool btn-bottom" title="Espelhar vertical"
                                @click="flipImage(false)">
                                <i class="ri-flip-vertical-line"></i>
                            </button>
                        </div>
                        <button type="button" class="btn-crop-action btn-crop-apply" :disabled="!isCropValid"
                            @click="applyCrop">
                            <i class="ri-check-line"></i> Recortar
                        </button>
                    </div>
                </transition>

            </div>

        </div>
    </transition>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import EmojiPickerField from '@/components/forms/EmojiPickerField.vue';

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    imageFile: { type: Object, default: null },
});

const emit = defineEmits(['save', 'close']);

// Define o conteúdo inicial de um contenteditable uma única vez (evita salto de cursor)
const vInitText = {
    mounted(el, binding) { el.innerText = binding.value || ''; },
};

// --- Refs DOM ---
const stageRef = ref(null);
const wrapperRef = ref(null);
const canvasRef = ref(null);

// --- Estado das ferramentas ---
const tool = ref('draw'); // 'draw' | 'text' | 'crop'
const colors = ['#5E5E5E', '#9E9E9E', '#FFFFFF', '#3897F0', '#3DC44B', '#A855F7', '#FF9500', '#FF3B30'];
const color = ref('#FFFFFF');
const lineWidth = ref(8);
const isEmojiOpen = ref(false);

// Seletor de cor (espectro)
const isColorPickerOpen = ref(false);
const isFontOpen = ref(false);
const hexInput = ref('#FFFFFF');
const spectrumRef = ref(null);
let spectrumDragging = false;

// Tamanho de exibição atual do canvas (reativo, para posicionar caixas de texto)
const dispW = ref(0);
const dispH = ref(0);

// --- Histórico (undo) para desenho/recorte ---
const history = ref([]);
const canUndo = computed(() => history.value.length > 1);

let ctx = null;
let isDrawing = false;

// --- Crop ---
const cropRect = ref(null); // { x, y, w, h } em px de exibição
const cropHandles = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CROP_MIN = 24;
let cropDrag = null;
const isCropValid = computed(() => cropRect.value && cropRect.value.w > CROP_MIN && cropRect.value.h > CROP_MIN);

const cropRectStyle = computed(() => {
    if (!cropRect.value) return {};
    return {
        left: cropRect.value.x + 'px',
        top: cropRect.value.y + 'px',
        width: cropRect.value.w + 'px',
        height: cropRect.value.h + 'px',
    };
});

// --- Texto ---
const TEXT_FONT_SIZE = 28;       // px de exibição
const TEXT_PAD_X = 12;
const TEXT_PAD_Y = 6;
const TEXT_RADIUS = 8;
const fonts = [
    { label: 'Sans Serif', value: 'sans-serif' },
    { label: 'Serifada', value: 'Georgia, serif' },
    { label: 'Monoespaçada', value: 'monospace' },
    { label: 'Manuscrita', value: 'cursive' },
];
const font = ref('sans-serif');
const bg = ref(false);
const textBoxes = ref([]); // { id, nx, ny, value, color, font, bg, size }
const selectedTextId = ref(null);
let textIdSeq = 0;
let textDrag = null;
let textResize = null;
let textRotate = null;

const fontLabel = computed(() => fonts.find(f => f.value === font.value)?.label || 'Sans Serif');
const selectedBox = computed(() => textBoxes.value.find(b => b.id === selectedTextId.value) || null);

// --- Formas ---
// Objetos editáveis sobrepostos ao canvas (como as caixas de texto), rasterizados ao concluir/girar/espelhar.
// box (rect/ellipse): { id, type, nx, ny, nw, nh, rotation, color, lineWidth, fill }  — coords normalizadas 0..1
// segmento (line/arrow): { id, type, n1x, n1y, n2x, n2y, color, lineWidth }
const SHAPE_MIN = 6; // px de exibição mínimos para não descartar a forma criada
const shapeTypes = [
    { value: 'arrow', icon: 'ri-arrow-right-up-line', label: 'Seta' },
    { value: 'line', icon: 'ri-subtract-line', label: 'Linha' },
    { value: 'rect', icon: 'ri-rectangle-line', label: 'Retângulo' },
    { value: 'ellipse', icon: 'ri-circle-line', label: 'Elipse' },
];
const shapeType = ref('arrow');        // tipo a ser criado
const shapeFill = ref(false);          // preenchimento padrão (rect/ellipse)
const shapeBoxes = ref([]);
const selectedShapeId = ref(null);
let shapeIdSeq = 0;
let shapeCreate = null;                // sessão de criação por arrasto
let shapeDrag = null;                  // sessão de mover
let shapeResize = null;                // sessão de redimensionar (cantos)
let shapeRotate = null;                // sessão de girar
let shapeEndpoint = null;              // sessão de mover extremo (line/arrow)

const isBoxShape = (t) => t === 'rect' || t === 'ellipse';
const selectedShape = computed(() => shapeBoxes.value.find(b => b.id === selectedShapeId.value) || null);
const fillApplicable = computed(() => isBoxShape(selectedShape.value ? selectedShape.value.type : shapeType.value));
const fillActive = computed(() => (selectedShape.value ? !!selectedShape.value.fill : shapeFill.value));

// Ajustar a espessura reflete na forma selecionada (modo Formas)
watch(lineWidth, (v) => {
    if (tool.value === 'shape' && selectedShape.value) selectedShape.value.lineWidth = v;
});

// ===== Inicialização =====
watch(() => props.isOpen, (open) => {
    if (open && props.imageFile) {
        nextTick(() => loadImage(props.imageFile));
    } else if (!open) {
        resetState();
    }
});

const resetState = () => {
    history.value = [];
    cropRect.value = null;
    cropDrag = null;
    textBoxes.value = [];
    selectedTextId.value = null;
    textDrag = null;
    textResize = null;
    textRotate = null;
    shapeBoxes.value = [];
    selectedShapeId.value = null;
    shapeType.value = 'arrow';
    shapeFill.value = false;
    shapeCreate = null;
    shapeDrag = null;
    shapeResize = null;
    shapeRotate = null;
    shapeEndpoint = null;
    isEmojiOpen.value = false;
    isColorPickerOpen.value = false;
    isFontOpen.value = false;
    isDrawing = false;
    tool.value = 'draw';
    color.value = '#FFFFFF';
    font.value = 'sans-serif';
    bg.value = false;
    lineWidth.value = 8;
    ctx = null;
};

const loadImage = (file) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        const canvas = canvasRef.value;
        if (!canvas) { URL.revokeObjectURL(url); return; }

        const MAX_DIM = 1920;
        let w = img.naturalWidth;
        let h = img.naturalHeight;
        if (w > MAX_DIM || h > MAX_DIM) {
            const scale = Math.min(MAX_DIM / w, MAX_DIM / h);
            w = Math.round(w * scale);
            h = Math.round(h * scale);
        }

        canvas.width = w;
        canvas.height = h;
        ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);

        fitCanvasToStage();
        pushHistory();
        URL.revokeObjectURL(url);
    };
    img.src = url;
};

const fitCanvasToStage = () => {
    const canvas = canvasRef.value;
    const stage = stageRef.value;
    if (!canvas || !stage) return;

    const availW = stage.clientWidth;
    const availH = stage.clientHeight;
    const scale = Math.min(availW / canvas.width, availH / canvas.height, 1);
    const w = Math.round(canvas.width * scale);
    const h = Math.round(canvas.height * scale);

    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    if (wrapperRef.value) {
        wrapperRef.value.style.width = w + 'px';
        wrapperRef.value.style.height = h + 'px';
    }
    dispW.value = w;
    dispH.value = h;
};

// ===== Histórico =====
const pushHistory = () => {
    const canvas = canvasRef.value;
    if (!canvas) return;
    history.value.push({ url: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height });
};

const undo = () => {
    if (!canUndo.value) return;
    history.value.pop();
    restoreState(history.value[history.value.length - 1]);
};

const restoreState = (state) => {
    const canvas = canvasRef.value;
    if (!canvas || !state) return;
    const img = new Image();
    img.onload = () => {
        canvas.width = state.width;
        canvas.height = state.height;
        ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        fitCanvasToStage();
    };
    img.src = state.url;
};

// ===== Ferramentas =====
const setTool = (t) => {
    isColorPickerOpen.value = false;
    isFontOpen.value = false;
    if (t !== 'text') {
        isEmojiOpen.value = false;
        cleanupEmptyBoxes();
        selectedTextId.value = null;
    }
    if (t !== 'shape') selectedShapeId.value = null;
    if (t !== 'crop') cropRect.value = null;
    tool.value = t;
    if (t === 'crop') nextTick(initCrop);
    else if (t === 'text') nextTick(openCenteredText);
};

const getCanvasDisplaySize = () => {
    const rect = canvasRef.value.getBoundingClientRect();
    return { w: rect.width, h: rect.height };
};

const getCanvasPoint = (e) => {
    const rect = canvasRef.value.getBoundingClientRect();
    return {
        x: (e.clientX - rect.left) * (canvasRef.value.width / rect.width),
        y: (e.clientY - rect.top) * (canvasRef.value.height / rect.height),
    };
};

const getScaleFactor = () => {
    const rect = canvasRef.value.getBoundingClientRect();
    return canvasRef.value.width / rect.width;
};

const contrastColor = (hex) => {
    const c = hex.replace('#', '');
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? '#000000' : '#FFFFFF';
};

// Clicar fora de uma caixa desseleciona (mantém o texto); descarta caixas vazias
const deselectText = () => {
    if (tool.value !== 'text') return;
    const el = document.activeElement;
    if (el && el.classList && el.classList.contains('text-edit')) el.blur();
    cleanupEmptyBoxes();
    selectedTextId.value = null;
};

const deselectShape = () => {
    if (tool.value !== 'shape') return;
    selectedShapeId.value = null;
};

// Clique em área vazia do palco (fora do canvas): desseleciona texto e forma
const onStageDown = () => {
    deselectText();
    deselectShape();
};

// ----- Desenho -----
const onPointerDown = (e) => {
    if (tool.value === 'text') { deselectText(); return; }
    if (tool.value !== 'draw' || !ctx) return;
    isDrawing = true;
    const p = getCanvasPoint(e);
    const factor = getScaleFactor();
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color.value;
    ctx.lineWidth = lineWidth.value * factor;
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
};

const onPointerMove = (e) => {
    if (!isDrawing || tool.value !== 'draw' || !ctx) return;
    const p = getCanvasPoint(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
};

const onPointerUp = () => {
    if (!isDrawing) return;
    isDrawing = false;
    ctx.closePath();
    pushHistory();
};

// ----- Crop -----
const initCrop = () => {
    const { w, h } = getCanvasDisplaySize();
    cropRect.value = { x: 0, y: 0, w, h };
};

const resetCrop = () => {
    cropDrag = null;
    initCrop();
};

const startMoveCrop = (e) => {
    const { w, h } = getCanvasDisplaySize();
    cropDrag = { mode: 'move', startX: e.clientX, startY: e.clientY, startRect: { ...cropRect.value }, dispW: w, dispH: h };
};

const startResizeCrop = (e, handle) => {
    const { w, h } = getCanvasDisplaySize();
    cropDrag = { mode: 'resize', handle, startX: e.clientX, startY: e.clientY, startRect: { ...cropRect.value }, dispW: w, dispH: h };
};

const onOverlayPointerMove = (e) => {
    if (tool.value !== 'crop' || !cropDrag) return;
    const dx = e.clientX - cropDrag.startX;
    const dy = e.clientY - cropDrag.startY;
    if (cropDrag.mode === 'move') applyCropMove(dx, dy);
    else applyCropResize(dx, dy);
};

const onOverlayPointerUp = () => { cropDrag = null; };

const applyCropMove = (dx, dy) => {
    const r = cropDrag.startRect;
    const x = Math.max(0, Math.min(r.x + dx, cropDrag.dispW - r.w));
    const y = Math.max(0, Math.min(r.y + dy, cropDrag.dispH - r.h));
    cropRect.value = { x, y, w: r.w, h: r.h };
};

const applyCropResize = (dx, dy) => {
    const r = cropDrag.startRect;
    const handle = cropDrag.handle;
    let left = r.x, top = r.y, right = r.x + r.w, bottom = r.y + r.h;
    if (handle.includes('w')) left = r.x + dx;
    if (handle.includes('e')) right = r.x + r.w + dx;
    if (handle.includes('n')) top = r.y + dy;
    if (handle.includes('s')) bottom = r.y + r.h + dy;
    left = Math.max(0, Math.min(left, right - CROP_MIN));
    top = Math.max(0, Math.min(top, bottom - CROP_MIN));
    right = Math.min(cropDrag.dispW, Math.max(right, left + CROP_MIN));
    bottom = Math.min(cropDrag.dispH, Math.max(bottom, top + CROP_MIN));
    cropRect.value = { x: left, y: top, w: right - left, h: bottom - top };
};

const applyCrop = () => {
    if (!isCropValid.value || !ctx) return;
    const canvas = canvasRef.value;

    // Queima formas e textos no raster antes de recortar (mantém o que está dentro da área)
    rasterizeShapes();
    rasterizeTextBoxes();

    const factor = getScaleFactor();
    const sx = Math.round(cropRect.value.x * factor);
    const sy = Math.round(cropRect.value.y * factor);
    const sw = Math.round(cropRect.value.w * factor);
    const sh = Math.round(cropRect.value.h * factor);

    const cropped = document.createElement('canvas');
    cropped.width = sw;
    cropped.height = sh;
    cropped.getContext('2d').drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);

    canvas.width = sw;
    canvas.height = sh;
    ctx = canvas.getContext('2d');
    ctx.drawImage(cropped, 0, 0);

    cropRect.value = null;
    fitCanvasToStage();
    pushHistory();
};

// ----- Girar / espelhar a imagem -----
const rotateImage = (deg) => {
    if (!ctx) return;
    rasterizeShapes();    // queima as formas para girarem junto
    rasterizeTextBoxes(); // queima os textos para girarem junto
    const canvas = canvasRef.value;
    const w = canvas.width;
    const h = canvas.height;
    const tmp = document.createElement('canvas');
    tmp.width = h;
    tmp.height = w;
    const tctx = tmp.getContext('2d');
    tctx.translate(h / 2, w / 2);
    tctx.rotate(deg * Math.PI / 180);
    tctx.drawImage(canvas, -w / 2, -h / 2);

    canvas.width = tmp.width;
    canvas.height = tmp.height;
    ctx = canvas.getContext('2d');
    ctx.drawImage(tmp, 0, 0);

    fitCanvasToStage();
    if (tool.value === 'crop') initCrop();
    pushHistory();
};

const flipImage = (horizontal) => {
    if (!ctx) return;
    rasterizeShapes();
    rasterizeTextBoxes();
    const canvas = canvasRef.value;
    const w = canvas.width;
    const h = canvas.height;
    const tmp = document.createElement('canvas');
    tmp.width = w;
    tmp.height = h;
    const tctx = tmp.getContext('2d');
    if (horizontal) { tctx.translate(w, 0); tctx.scale(-1, 1); }
    else { tctx.translate(0, h); tctx.scale(1, -1); }
    tctx.drawImage(canvas, 0, 0);

    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(tmp, 0, 0);

    if (tool.value === 'crop') initCrop();
    pushHistory();
};

// ----- Texto: criação / seleção / edição -----
const cleanupEmptyBoxes = () => {
    textBoxes.value = textBoxes.value.filter(b => (b.value || '').trim().length > 0);
};

const createTextBox = (nx, ny) => {
    cleanupEmptyBoxes();
    const boxBg = bg.value;
    const box = { id: ++textIdSeq, nx, ny, value: '', color: color.value, font: font.value, bg: boxBg, size: TEXT_FONT_SIZE, rotation: 0 };
    textBoxes.value.push(box);
    selectedTextId.value = box.id;
    nextTick(() => document.getElementById('ta-' + box.id)?.focus());
    return box;
};

// Cria uma nova caixa de texto centralizada (acionado apenas pela ferramenta Texto)
const openCenteredText = () => {
    createTextBox(0.32, 0.30);
};

const selectText = (box) => {
    selectedTextId.value = box.id;
    color.value = box.color;
    font.value = box.font;
    bg.value = box.bg;
};

const onTextInput = (e, box) => {
    box.value = e.target.innerText;
};

// ----- Texto: mover (arrastando o corpo do texto) -----
// Toque simples = edita; arrastar = move o campo.
const onTextBodyDown = (e, box) => {
    if (tool.value !== 'text') return;
    selectText(box);
    textDrag = { id: box.id, startX: e.clientX, startY: e.clientY, startNx: box.nx, startNy: box.ny, moved: false, captured: false };
};

const onTextBodyMove = (e) => {
    if (!textDrag) return;
    const dx = e.clientX - textDrag.startX;
    const dy = e.clientY - textDrag.startY;
    if (!textDrag.moved) {
        if (Math.hypot(dx, dy) < 5) return;
        textDrag.moved = true;
        try { e.currentTarget.setPointerCapture(e.pointerId); textDrag.captured = true; } catch (_) { /* noop */ }
        document.getElementById('ta-' + textDrag.id)?.blur();
        window.getSelection()?.removeAllRanges();
    }
    e.preventDefault();
    const box = textBoxes.value.find(b => b.id === textDrag.id);
    if (!box) return;
    box.nx = Math.max(0, Math.min(textDrag.startNx + dx / dispW.value, 1));
    box.ny = Math.max(0, Math.min(textDrag.startNy + dy / dispH.value, 1));
};

const onTextBodyUp = (e) => {
    if (!textDrag) return;
    if (textDrag.captured) { try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ } }
    if (!textDrag.moved) document.getElementById('ta-' + textDrag.id)?.focus();
    textDrag = null;
};

// ----- Texto: girar (alça superior) -----
const startTextRotate = (e, box) => {
    selectText(box);
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    textRotate = { id: box.id, cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2 };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
};

const onTextRotateMove = (e) => {
    if (!textRotate) return;
    const box = textBoxes.value.find(b => b.id === textRotate.id);
    if (!box) return;
    const ang = Math.atan2(e.clientY - textRotate.cy, e.clientX - textRotate.cx) * 180 / Math.PI;
    box.rotation = Math.round(ang + 90);
};

const onTextRotateEnd = (e) => {
    if (!textRotate) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    textRotate = null;
};

// ----- Texto: redimensionar (proporção) -----
const startTextResize = (e, box) => {
    selectedTextId.value = box.id;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
    textResize = { id: box.id, startX: e.clientX, startY: e.clientY, startSize: box.size || TEXT_FONT_SIZE };
};

const onTextResizeMove = (e) => {
    if (!textResize) return;
    const box = textBoxes.value.find(b => b.id === textResize.id);
    if (!box) return;
    const delta = ((e.clientX - textResize.startX) + (e.clientY - textResize.startY)) / 2;
    box.size = Math.max(12, Math.min(textResize.startSize + delta, 200));
};

const onTextResizeEnd = (e) => {
    if (!textResize) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    textResize = null;
};

// ----- Texto: controles -----
const applyColor = (c) => {
    color.value = c;
    hexInput.value = c;
    if (selectedBox.value) selectedBox.value.color = c;
    if (selectedShape.value) selectedShape.value.color = c;
};

// ----- Seletor de cor (espectro completo) -----
const toRgbHex = (n) => n.toString(16).padStart(2, '0');

const toggleColorPicker = () => {
    isColorPickerOpen.value = !isColorPickerOpen.value;
    if (isColorPickerOpen.value) {
        isFontOpen.value = false;
        isEmojiOpen.value = false;
        hexInput.value = color.value;
        nextTick(drawSpectrum);
    }
};

const drawSpectrum = () => {
    const canvas = spectrumRef.value;
    if (!canvas) return;
    const c = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    // Faixa de matiz (horizontal)
    const hue = c.createLinearGradient(0, 0, w, 0);
    hue.addColorStop(0, '#ff0000');
    hue.addColorStop(0.16, '#ffff00');
    hue.addColorStop(0.33, '#00ff00');
    hue.addColorStop(0.50, '#00ffff');
    hue.addColorStop(0.66, '#0000ff');
    hue.addColorStop(0.83, '#ff00ff');
    hue.addColorStop(1, '#ff0000');
    c.fillStyle = hue;
    c.fillRect(0, 0, w, h);

    // Branco no topo, preto embaixo (vertical)
    const val = c.createLinearGradient(0, 0, 0, h);
    val.addColorStop(0, 'rgba(255,255,255,1)');
    val.addColorStop(0.5, 'rgba(255,255,255,0)');
    val.addColorStop(0.5, 'rgba(0,0,0,0)');
    val.addColorStop(1, 'rgba(0,0,0,1)');
    c.fillStyle = val;
    c.fillRect(0, 0, w, h);
};

const pickFromSpectrum = (e) => {
    const canvas = spectrumRef.value;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(Math.round((e.clientX - rect.left) * (canvas.width / rect.width)), canvas.width - 1));
    const y = Math.max(0, Math.min(Math.round((e.clientY - rect.top) * (canvas.height / rect.height)), canvas.height - 1));
    const p = canvas.getContext('2d').getImageData(x, y, 1, 1).data;
    applyColor(`#${toRgbHex(p[0])}${toRgbHex(p[1])}${toRgbHex(p[2])}`.toUpperCase());
};

const onSpectrumDown = (e) => { spectrumDragging = true; pickFromSpectrum(e); };
const onSpectrumMove = (e) => { if (spectrumDragging) pickFromSpectrum(e); };
const onSpectrumUp = () => { spectrumDragging = false; };

const applyHex = () => {
    let v = (hexInput.value || '').trim();
    if (!v.startsWith('#')) v = '#' + v;
    if (/^#[0-9a-fA-F]{6}$/.test(v)) {
        applyColor(v.toUpperCase());
    } else {
        hexInput.value = color.value; // restaura se inválido
    }
};

const applyFont = (f) => {
    font.value = f;
    if (selectedBox.value) selectedBox.value.font = f;
};

const toggleFontMenu = () => {
    isFontOpen.value = !isFontOpen.value;
    if (isFontOpen.value) isColorPickerOpen.value = false;
};

const selectFont = (value) => {
    applyFont(value);
    isFontOpen.value = false;
};

const toggleBg = () => {
    bg.value = !bg.value;
    if (selectedBox.value) selectedBox.value.bg = bg.value;
};

const deleteSelectedText = () => {
    if (!selectedBox.value) return;
    const id = selectedBox.value.id;
    textBoxes.value = textBoxes.value.filter(b => b.id !== id);
    selectedTextId.value = null;
};

const toggleEmoji = () => {
    isEmojiOpen.value = !isEmojiOpen.value;
    if (isEmojiOpen.value) {
        isColorPickerOpen.value = false;
        isFontOpen.value = false;
        if (!selectedBox.value) openCenteredText();
    }
};

const onSelectEmoji = (emoji) => {
    let box = selectedBox.value;
    if (!box) { box = createTextBox(0.32, 0.44); box.value = emoji.i; return; }
    const el = document.getElementById('ta-' + box.id);
    if (el) {
        el.focus();
        document.execCommand('insertText', false, emoji.i);
        box.value = el.innerText;
    } else {
        box.value += emoji.i;
    }
};

// ----- Estilos dinâmicos das caixas de texto -----
const boxWrapperStyle = (box) => ({
    left: (box.nx * dispW.value) + 'px',
    top: (box.ny * dispH.value) + 'px',
    transform: `rotate(${box.rotation || 0}deg)`,
    transformOrigin: 'center center',
});

const textEditStyle = (box) => ({
    color: box.bg ? contrastColor(box.color) : box.color,
    backgroundColor: box.bg ? box.color : 'transparent',
    fontFamily: box.font,
    fontSize: (box.size || TEXT_FONT_SIZE) + 'px',
    padding: `${TEXT_PAD_Y}px ${TEXT_PAD_X}px`,
    borderRadius: TEXT_RADIUS + 'px',
    textShadow: box.bg ? 'none' : '0 1px 3px rgba(0,0,0,0.45)',
});

// ----- Renderiza as caixas de texto no canvas -----
const rasterizeTextBoxes = () => {
    if (!ctx || !textBoxes.value.length) { textBoxes.value = []; selectedTextId.value = null; return; }
    const canvas = canvasRef.value;
    const factor = canvas.width / dispW.value;
    const padX = TEXT_PAD_X * factor;
    const padY = TEXT_PAD_Y * factor;

    textBoxes.value.forEach((box) => {
        const value = (box.value || '').replace(/\s+$/, '');
        if (!value) return;
        const fontPx = (box.size || TEXT_FONT_SIZE) * factor;
        const lineHeight = fontPx * 1.25;
        ctx.font = `600 ${fontPx}px ${box.font}`;
        ctx.textBaseline = 'top';
        const lines = value.split('\n');
        const maxW = Math.max(...lines.map(l => ctx.measureText(l).width), 1);
        const rectW = maxW + padX * 2;
        const rectH = lines.length * lineHeight + padY * 2;
        const cx = box.nx * canvas.width + rectW / 2;
        const cy = box.ny * canvas.height + rectH / 2;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate((box.rotation || 0) * Math.PI / 180);
        ctx.translate(-rectW / 2, -rectH / 2);

        if (box.bg) {
            ctx.fillStyle = box.color;
            ctx.beginPath();
            ctx.roundRect(0, 0, rectW, rectH, TEXT_RADIUS * factor);
            ctx.fill();
            ctx.fillStyle = contrastColor(box.color);
        } else {
            ctx.fillStyle = box.color;
            ctx.shadowColor = 'rgba(0,0,0,0.45)';
            ctx.shadowBlur = fontPx * 0.12;
        }
        lines.forEach((line, i) => ctx.fillText(line, padX, padY + i * lineHeight));
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.restore();
    });

    textBoxes.value = [];
    selectedTextId.value = null;
};

// ===== Formas (objetos editáveis sobre o canvas) =====
const clamp01 = (v) => Math.max(0, Math.min(v, 1));
const cloneShape = (box) => ({ ...box });

// Ponto do cursor em px de exibição, relativo ao wrapper e limitado à área do canvas
const getDispPoint = (e) => {
    const rect = wrapperRef.value.getBoundingClientRect();
    return {
        x: Math.max(0, Math.min(e.clientX - rect.left, dispW.value)),
        y: Math.max(0, Math.min(e.clientY - rect.top, dispH.value)),
    };
};

// ----- Geometria (px de exibição) usada pelo SVG -----
const boxPx = (box) => ({
    x: box.nx * dispW.value,
    y: box.ny * dispH.value,
    w: box.nw * dispW.value,
    h: box.nh * dispH.value,
});
const segPx = (box) => ({
    x1: box.n1x * dispW.value,
    y1: box.n1y * dispH.value,
    x2: box.n2x * dispW.value,
    y2: box.n2y * dispH.value,
});
const rotAttr = (box) => {
    if (!box.rotation) return null;
    const b = boxPx(box);
    return `rotate(${box.rotation} ${b.x + b.w / 2} ${b.y + b.h / 2})`;
};

// Pontos da ponta da seta (compartilhado por SVG e rasterização)
const arrowHead = (x1, y1, x2, y2, size) => {
    const ang = Math.atan2(y2 - y1, x2 - x1);
    const a1 = ang - Math.PI / 6;
    const a2 = ang + Math.PI / 6;
    return [
        [x2, y2],
        [x2 - size * Math.cos(a1), y2 - size * Math.sin(a1)],
        [x2 - size * Math.cos(a2), y2 - size * Math.sin(a2)],
    ];
};
const arrowHeadSize = (lw) => lw * 3 + 6;
const arrowHeadPoints = (box) => {
    const s = segPx(box);
    return arrowHead(s.x1, s.y1, s.x2, s.y2, arrowHeadSize(box.lineWidth))
        .map((p) => `${p[0]},${p[1]}`).join(' ');
};

const isShapeTooSmall = (box) => {
    if (isBoxShape(box.type)) {
        return box.nw * dispW.value < SHAPE_MIN && box.nh * dispH.value < SHAPE_MIN;
    }
    const dx = (box.n2x - box.n1x) * dispW.value;
    const dy = (box.n2y - box.n1y) * dispH.value;
    return Math.hypot(dx, dy) < SHAPE_MIN;
};

// ----- Criação por arrasto (overlay) -----
const onShapeCreateDown = (e) => {
    if (tool.value !== 'shape') return;
    deselectShape();
    const p = getDispPoint(e);
    const nx = p.x / dispW.value;
    const ny = p.y / dispH.value;
    const base = { id: ++shapeIdSeq, type: shapeType.value, color: color.value, lineWidth: lineWidth.value };
    const box = isBoxShape(shapeType.value)
        ? { ...base, nx, ny, nw: 0, nh: 0, rotation: 0, fill: shapeFill.value }
        : { ...base, n1x: nx, n1y: ny, n2x: nx, n2y: ny };
    shapeBoxes.value.push(box);
    shapeCreate = { id: box.id, startX: p.x, startY: p.y };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
};

const onShapeCreateMove = (e) => {
    if (!shapeCreate) return;
    const box = shapeBoxes.value.find((b) => b.id === shapeCreate.id);
    if (!box) return;
    const p = getDispPoint(e);
    if (isBoxShape(box.type)) {
        box.nx = Math.min(shapeCreate.startX, p.x) / dispW.value;
        box.ny = Math.min(shapeCreate.startY, p.y) / dispH.value;
        box.nw = Math.abs(p.x - shapeCreate.startX) / dispW.value;
        box.nh = Math.abs(p.y - shapeCreate.startY) / dispH.value;
    } else {
        box.n2x = p.x / dispW.value;
        box.n2y = p.y / dispH.value;
    }
};

const onShapeCreateUp = (e) => {
    if (!shapeCreate) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    const box = shapeBoxes.value.find((b) => b.id === shapeCreate.id);
    shapeCreate = null;
    if (box && isShapeTooSmall(box)) {
        shapeBoxes.value = shapeBoxes.value.filter((b) => b.id !== box.id);
        return;
    }
    if (box) selectShape(box);
};

// ----- Seleção -----
const selectShape = (box) => {
    selectedShapeId.value = box.id;
    color.value = box.color;
    hexInput.value = box.color;
    lineWidth.value = box.lineWidth;
    if (isBoxShape(box.type)) shapeFill.value = !!box.fill;
};

// ----- Mover (arrastando o corpo da forma) -----
const onShapeBodyDown = (e, box) => {
    if (tool.value !== 'shape') return;
    selectShape(box);
    const p = getDispPoint(e);
    shapeDrag = { id: box.id, startX: p.x, startY: p.y, snapshot: cloneShape(box) };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
};

const onShapeBodyMove = (e) => {
    if (!shapeDrag) return;
    const box = shapeBoxes.value.find((b) => b.id === shapeDrag.id);
    if (!box) return;
    const p = getDispPoint(e);
    const dnx = (p.x - shapeDrag.startX) / dispW.value;
    const dny = (p.y - shapeDrag.startY) / dispH.value;
    const s = shapeDrag.snapshot;
    if (isBoxShape(box.type)) {
        box.nx = Math.max(0, Math.min(s.nx + dnx, 1 - s.nw));
        box.ny = Math.max(0, Math.min(s.ny + dny, 1 - s.nh));
    } else {
        box.n1x = clamp01(s.n1x + dnx);
        box.n1y = clamp01(s.n1y + dny);
        box.n2x = clamp01(s.n2x + dnx);
        box.n2y = clamp01(s.n2y + dny);
    }
};

const onShapeBodyUp = (e) => {
    if (!shapeDrag) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    shapeDrag = null;
};

// ----- Redimensionar caixa (cantos) — mantém o canto oposto fixo, com suporte a rotação -----
const startShapeResize = (e, handle, box) => {
    selectShape(box);
    const b = boxPx(box);
    const cx = b.x + b.w / 2;
    const cy = b.y + b.h / 2;
    const th = (box.rotation || 0) * Math.PI / 180;
    const cos = Math.cos(th);
    const sin = Math.sin(th);
    // canto oposto (local) ao arrastado
    const opp = { nw: [b.w / 2, b.h / 2], ne: [-b.w / 2, b.h / 2], se: [-b.w / 2, -b.h / 2], sw: [b.w / 2, -b.h / 2] }[handle];
    const ox = cx + opp[0] * cos - opp[1] * sin;
    const oy = cy + opp[0] * sin + opp[1] * cos;
    shapeResize = { id: box.id, th, ox, oy };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
};

const onShapeResizeMove = (e) => {
    if (!shapeResize) return;
    const box = shapeBoxes.value.find((b) => b.id === shapeResize.id);
    if (!box) return;
    const p = getDispPoint(e);
    const dx = p.x - shapeResize.ox;
    const dy = p.y - shapeResize.oy;
    const cos = Math.cos(shapeResize.th);
    const sin = Math.sin(shapeResize.th);
    const lx = dx * cos + dy * sin;   // referencial local da caixa
    const ly = -dx * sin + dy * cos;
    const w = Math.max(Math.abs(lx), 8);
    const h = Math.max(Math.abs(ly), 8);
    const cx = (shapeResize.ox + p.x) / 2;
    const cy = (shapeResize.oy + p.y) / 2;
    box.nw = w / dispW.value;
    box.nh = h / dispH.value;
    box.nx = (cx - w / 2) / dispW.value;
    box.ny = (cy - h / 2) / dispH.value;
};

const onShapeResizeEnd = (e) => {
    if (!shapeResize) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    shapeResize = null;
};

// ----- Girar caixa -----
const startShapeRotate = (e, box) => {
    selectShape(box);
    const rect = wrapperRef.value.getBoundingClientRect();
    shapeRotate = {
        id: box.id,
        cx: rect.left + (box.nx + box.nw / 2) * dispW.value,
        cy: rect.top + (box.ny + box.nh / 2) * dispH.value,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
};

const onShapeRotateMove = (e) => {
    if (!shapeRotate) return;
    const box = shapeBoxes.value.find((b) => b.id === shapeRotate.id);
    if (!box) return;
    const ang = Math.atan2(e.clientY - shapeRotate.cy, e.clientX - shapeRotate.cx) * 180 / Math.PI;
    box.rotation = Math.round(ang + 90);
};

const onShapeRotateEnd = (e) => {
    if (!shapeRotate) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    shapeRotate = null;
};

// ----- Mover extremos de linha/seta -----
const startShapeEndpoint = (e, box, which) => {
    selectShape(box);
    shapeEndpoint = { id: box.id, which };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
};

const onShapeEndpointMove = (e) => {
    if (!shapeEndpoint) return;
    const box = shapeBoxes.value.find((b) => b.id === shapeEndpoint.id);
    if (!box) return;
    const p = getDispPoint(e);
    const nx = clamp01(p.x / dispW.value);
    const ny = clamp01(p.y / dispH.value);
    if (shapeEndpoint.which === 1) { box.n1x = nx; box.n1y = ny; }
    else { box.n2x = nx; box.n2y = ny; }
};

const onShapeEndpointEnd = (e) => {
    if (!shapeEndpoint) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* noop */ }
    shapeEndpoint = null;
};

// Posições (px de exibição) dos handles da forma selecionada
const selectedHandles = computed(() => {
    const box = selectedShape.value;
    if (!box || tool.value !== 'shape') return null;
    if (isBoxShape(box.type)) {
        const b = boxPx(box);
        const cx = b.x + b.w / 2;
        const cy = b.y + b.h / 2;
        const th = (box.rotation || 0) * Math.PI / 180;
        const cos = Math.cos(th);
        const sin = Math.sin(th);
        const at = (lx, ly) => ({ x: cx + lx * cos - ly * sin, y: cy + lx * sin + ly * cos });
        const corners = [
            { key: 'nw', ...at(-b.w / 2, -b.h / 2) },
            { key: 'ne', ...at(b.w / 2, -b.h / 2) },
            { key: 'se', ...at(b.w / 2, b.h / 2) },
            { key: 'sw', ...at(-b.w / 2, b.h / 2) },
        ];
        const top = at(0, -b.h / 2);
        const rotate = { x: top.x - sin * 26, y: top.y - cos * 26 };
        return { kind: 'box', corners, outline: corners.map((c) => `${c.x},${c.y}`).join(' '), top, rotate };
    }
    const s = segPx(box);
    return { kind: 'segment', p1: { x: s.x1, y: s.y1 }, p2: { x: s.x2, y: s.y2 } };
});

// ----- Controles -----
const setShapeType = (t) => { shapeType.value = t; };

const toggleShapeFill = () => {
    shapeFill.value = !shapeFill.value;
    if (selectedShape.value && isBoxShape(selectedShape.value.type)) selectedShape.value.fill = shapeFill.value;
};

const deleteSelectedShape = () => {
    if (!selectedShape.value) return;
    const id = selectedShape.value.id;
    shapeBoxes.value = shapeBoxes.value.filter((b) => b.id !== id);
    selectedShapeId.value = null;
};

// ----- Rasteriza as formas no canvas -----
const rasterizeShapes = () => {
    if (!ctx || !shapeBoxes.value.length || !dispW.value) { shapeBoxes.value = []; selectedShapeId.value = null; return; }
    const canvas = canvasRef.value;
    const factor = canvas.width / dispW.value;

    shapeBoxes.value.forEach((box) => {
        ctx.save();
        ctx.strokeStyle = box.color;
        ctx.fillStyle = box.color;
        ctx.lineWidth = Math.max(box.lineWidth * factor, 1);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (isBoxShape(box.type)) {
            const w = box.nw * canvas.width;
            const h = box.nh * canvas.height;
            const cx = box.nx * canvas.width + w / 2;
            const cy = box.ny * canvas.height + h / 2;
            ctx.translate(cx, cy);
            ctx.rotate((box.rotation || 0) * Math.PI / 180);
            if (box.type === 'rect') {
                if (box.fill) ctx.fillRect(-w / 2, -h / 2, w, h);
                else ctx.strokeRect(-w / 2, -h / 2, w, h);
            } else {
                ctx.beginPath();
                ctx.ellipse(0, 0, Math.abs(w / 2), Math.abs(h / 2), 0, 0, Math.PI * 2);
                if (box.fill) ctx.fill(); else ctx.stroke();
            }
        } else {
            const x1 = box.n1x * canvas.width;
            const y1 = box.n1y * canvas.height;
            const x2 = box.n2x * canvas.width;
            const y2 = box.n2y * canvas.height;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            if (box.type === 'arrow') {
                const pts = arrowHead(x1, y1, x2, y2, arrowHeadSize(box.lineWidth) * factor);
                ctx.beginPath();
                ctx.moveTo(pts[0][0], pts[0][1]);
                ctx.lineTo(pts[1][0], pts[1][1]);
                ctx.lineTo(pts[2][0], pts[2][1]);
                ctx.closePath();
                ctx.fill();
            }
        }
        ctx.restore();
    });

    shapeBoxes.value = [];
    selectedShapeId.value = null;
};

// ===== Conclusão =====
const handleResize = () => {
    if (props.isOpen && canvasRef.value?.width) fitCanvasToStage();
};
onMounted(() => window.addEventListener('resize', handleResize));
onUnmounted(() => window.removeEventListener('resize', handleResize));

const cancel = () => emit('close');

const done = () => {
    cleanupEmptyBoxes();
    const canvas = canvasRef.value;
    if (!canvas || !props.imageFile) { emit('close'); return; }

    rasterizeShapes();
    rasterizeTextBoxes();

    const original = props.imageFile;
    const isPng = original.type === 'image/png';
    const mime = isPng ? 'image/png' : 'image/jpeg';
    const quality = isPng ? undefined : 0.92;

    canvas.toBlob((blob) => {
        if (!blob) { emit('close'); return; }
        const baseName = original.name.replace(/\.[^.]+$/, '');
        const ext = isPng ? 'png' : 'jpg';
        emit('save', new File([blob], `${baseName}.${ext}`, { type: mime }));
    }, mime, quality);
};
</script>

<style scoped>
.image-editor-overlay {
    position: absolute;
    inset: 0;
    z-index: 60;
    display: flex;
    flex-direction: column;
    background-color: #0b141a;
    user-select: none;
}

/* === Toolbar === */
.editor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    height: 60px;
    flex-shrink: 0;
}

.tools-group {
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    flex-grow: 1;
    max-width: 360px;
    margin: 0 auto;
}

.btn-tool {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #e9edef;
    font-size: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.18s, color 0.18s;
}

.btn-tool:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.12);
}

.btn-tool.active {
    background-color: #00a884;
    color: #fff;
}

.btn-tool:disabled {
    opacity: 0.35;
    cursor: default;
}

.btn-done {
    background-color: #00a884;
    color: #fff;
}

.btn-done:hover {
    background-color: #008f6f;
}

/* === Stage / Canvas === */
.editor-stage {
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 16px;
    min-height: 0;
}

.canvas-wrapper {
    position: relative;
    display: inline-flex;
}

.editor-canvas {
    display: block;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    touch-action: none;
}

/* === Camada de texto === */
.text-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 3;
}

.text-box {
    position: absolute;
    pointer-events: none;
    max-width: 90%;
}

.text-layer.interactive .text-box {
    pointer-events: auto;
}

.text-box.selected {
    outline: 2px solid #00d268;
    outline-offset: 2px;
    border-radius: 10px;
}

.text-rotate-handle {
    position: absolute;
    top: -26px;
    left: 50%;
    transform: translateX(-50%);
    width: 16px;
    height: 16px;
    background: #fff;
    border: 2px solid #00d268;
    border-radius: 50%;
    cursor: grab;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    touch-action: none;
}

.text-rotate-handle::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 2px;
    height: 10px;
    background: #00d268;
    transform: translateX(-50%);
}

.text-rotate-handle:active {
    cursor: grabbing;
}

.text-box .text-edit {
    cursor: move;
}

.text-box.selected .text-edit:focus {
    cursor: text;
}

.text-resize-handle {
    position: absolute;
    bottom: -9px;
    right: -9px;
    width: 16px;
    height: 16px;
    background: #fff;
    border: 2px solid #00d268;
    border-radius: 50%;
    cursor: nwse-resize;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    touch-action: none;
}

.text-edit {
    display: inline-block;
    min-width: 14px;
    font-weight: 600;
    line-height: 1.25;
    white-space: pre-wrap;
    word-break: break-word;
    outline: none;
    cursor: text;
}

.text-edit:empty::before {
    content: 'Texto';
    opacity: 0.6;
}

/* === Camada de formas === */
.shape-svg-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 2;
    overflow: visible;
}

.shape-svg-layer.interactive .shape-hit {
    pointer-events: all;
    cursor: move;
}

.shape-hit {
    fill: transparent;
    stroke: transparent;
    stroke-width: 14px;
}

.shape-create-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    touch-action: none;
    cursor: crosshair;
}

.shape-handles-layer {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 4;
    overflow: visible;
}

.shape-outline {
    fill: none;
    stroke: #00d268;
    stroke-width: 1.5px;
    stroke-dasharray: 5 4;
}

.shape-rotate-stem {
    stroke: #00d268;
    stroke-width: 2px;
}

.shape-handle {
    fill: #fff;
    stroke: #00d268;
    stroke-width: 2px;
    pointer-events: all;
    cursor: nwse-resize;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
}

.shape-handle-rotate {
    cursor: grab;
}

/* === Overlay de recorte === */
.interaction-overlay {
    position: absolute;
    inset: 0;
    touch-action: none;
    z-index: 5;
}

.crop-rect {
    position: absolute;
    border: 2px solid #fff;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
    box-sizing: border-box;
    cursor: move;
}

.crop-grid {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
        linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
        linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-position: 0 33.33%, 0 66.66%, 33.33% 0, 66.66% 0;
    background-size: 100% 1px, 100% 1px, 1px 100%, 1px 100%;
    background-repeat: no-repeat;
}

.crop-handle {
    position: absolute;
    width: 16px;
    height: 16px;
    background: #fff;
    border: 2px solid #00a884;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    touch-action: none;
}

.handle-nw {
    top: -9px;
    left: -9px;
    cursor: nwse-resize;
}

.handle-n {
    top: -9px;
    left: 50%;
    transform: translateX(-50%);
    cursor: ns-resize;
}

.handle-ne {
    top: -9px;
    right: -9px;
    cursor: nesw-resize;
}

.handle-e {
    top: 50%;
    right: -9px;
    transform: translateY(-50%);
    cursor: ew-resize;
}

.handle-se {
    bottom: -9px;
    right: -9px;
    cursor: nwse-resize;
}

.handle-s {
    bottom: -9px;
    left: 50%;
    transform: translateX(-50%);
    cursor: ns-resize;
}

.handle-sw {
    bottom: -9px;
    left: -9px;
    cursor: nesw-resize;
}

.handle-w {
    top: 50%;
    left: -9px;
    transform: translateY(-50%);
    cursor: ew-resize;
}

/* === Seletor de emoji === */
.editor-emoji-picker {
    position: absolute;
    bottom: 84px;
    right: 16px;
    z-index: 70;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    border-radius: 8px;
    overflow: hidden;
}

/* === Barra inferior === */
.editor-bottom {
    flex-shrink: 0;
    min-height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 16px 18px;
}

.control-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    flex-wrap: wrap;
    width: 100%;
    max-width: 820px;
}

.color-palette {
    display: flex;
    gap: 10px;
}

/* Seletor de tipo de forma */
.shape-type-group {
    display: flex;
    align-items: center;
    gap: 6px;
    padding-right: 6px;
    border-right: 1px solid rgba(255, 255, 255, 0.15);
}

.color-swatch {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.5);
    cursor: pointer;
    padding: 0;
    transition: transform 0.15s, border-color 0.15s;
}

.color-swatch:hover {
    transform: scale(1.12);
}

.color-swatch.active {
    border-color: #fff;
    box-shadow: 0 0 0 2px #00a884;
    transform: scale(1.12);
}

.swatch-custom {
    background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
    border-color: #fff;
}

/* Dropdown de fonte */
.font-dropdown {
    position: relative;
}

.font-menu {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    min-width: 170px;
    background-color: #233138;
    border-radius: 10px;
    padding: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    z-index: 80;
}

.font-option {
    display: block;
    width: 100%;
    text-align: left;
    padding: 9px 12px;
    border: none;
    background: transparent;
    color: #e9edef;
    font-size: 15px;
    border-radius: 6px;
    cursor: pointer;
}

.font-option:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.font-option.active {
    background-color: #00a884;
    color: #fff;
}

/* Painel do seletor de cor */
.color-picker-panel {
    position: absolute;
    bottom: 84px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #233138;
    border-radius: 12px;
    padding: 12px;
    z-index: 75;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
}

.spectrum-canvas {
    display: block;
    width: 260px;
    height: 150px;
    border-radius: 8px;
    cursor: crosshair;
    touch-action: none;
}

.hex-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
}

.hex-preview {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.4);
    flex-shrink: 0;
}

.hex-input {
    flex-grow: 1;
    background-color: rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    color: #e9edef;
    font-size: 14px;
    padding: 8px 12px;
    outline: none;
    font-family: monospace;
    text-transform: uppercase;
}

.hex-input:focus {
    border-color: #00a884;
}

.thickness-control {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #e9edef;
}

.thickness-control input[type="range"] {
    width: 120px;
    accent-color: #00a884;
    cursor: pointer;
}

.ctrl-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    height: 38px;
    padding: 0 14px;
    border-radius: 19px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: transparent;
    color: #e9edef;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.18s, border-color 0.18s;
}

.ctrl-pill:hover {
    background-color: rgba(255, 255, 255, 0.1);
}

.ctrl-pill.active {
    background-color: #00a884;
    border-color: #00a884;
    color: #fff;
}

.ctrl-pill i {
    font-size: 16px;
}

.bg-dot {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid currentColor;
    background: transparent;
}

.bg-dot.on {
    background: currentColor;
}

.btn-bottom {
    width: 38px;
    height: 38px;
    font-size: 20px;
}

/* === Ações de crop === */
.crop-actions {
    justify-content: space-between;
}

.crop-transform {
    display: flex;
    align-items: center;
    gap: 4px;
}

.crop-hint {
    color: #8696a0;
    font-size: 13px;
    text-align: center;
    flex-grow: 1;
}

.btn-crop-action {
    display: flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: 20px;
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.18s, background 0.18s;
}

.btn-crop-cancel {
    background-color: rgba(255, 255, 255, 0.12);
    color: #e9edef;
}

.btn-crop-cancel:hover {
    background-color: rgba(255, 255, 255, 0.2);
}

.btn-crop-apply {
    background-color: #00a884;
    color: #fff;
}

.btn-crop-apply:hover:not(:disabled) {
    background-color: #008f6f;
}

.btn-crop-apply:disabled {
    opacity: 0.4;
    cursor: default;
}

/* === Animações === */
.editor-fade-enter-active,
.editor-fade-leave-active {
    transition: opacity 0.2s ease;
}

.editor-fade-enter-from,
.editor-fade-leave-to {
    opacity: 0;
}

.editor-slide-enter-active,
.editor-slide-leave-active {
    transition: all 0.2s ease;
}

.editor-slide-enter-from,
.editor-slide-leave-to {
    opacity: 0;
    transform: translateY(8px);
}
</style>
